class Sidecart extends HTMLElement {
  constructor() {
    super();
    this.firstLoad = true;
    this._enforceRunning = false;
  }

  connectedCallback() {
    this.initEventListeners();
    this.initProgressBar();
    this.initSwipers();
    this.groupCartItems();
    this.setDynamicVH();
  }

  setDynamicVH() {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    setVH();
    window.addEventListener("resize", setVH);
    window.addEventListener("orientationchange", setVH);
  }

  initEventListeners() {
    if (this.firstLoad == true) {
      document.querySelectorAll("[cart-toggle]").forEach((button) => {
        button.addEventListener("click", this.toggleCart.bind(this));
        button.addEventListener("keydown", this.toggleCart.bind(this));
      });
    } else {
      this.querySelectorAll("[cart-toggle]").forEach((button) => {
        button.addEventListener("click", this.toggleCart.bind(this));
        button.addEventListener("keydown", this.toggleCart.bind(this));
      });
    }

    // Cadeaux : changement de parfum
    this.querySelectorAll(".gift-variant-select").forEach((select) => {
      select.addEventListener("change", (e) => this.handleGiftVariantChange(e));

      // Auto-select first variant if not in cart
      if (select.dataset.autoSelect === "true" && !select.dataset.itemKey) {
        const selectedOption = select.options[select.selectedIndex];
        if (selectedOption && selectedOption.value) {
          // Trigger auto-add after a short delay to ensure DOM is ready
          setTimeout(() => this.autoAddGiftVariant(select), 100);
        }
      }
    });

    const overlay = document.querySelector(".sidecart__overlay");
    if (overlay) {
      if (this.firstLoad) {
        overlay.addEventListener("click", this.toggleCart.bind(this));
        overlay.addEventListener("keydown", this.toggleCart.bind(this));
      }
    }

    document.querySelectorAll("[data-close-sidecart]").forEach((button) => {
      button.addEventListener("click", this.toggleCart.bind(this));
      button.addEventListener("keydown", this.toggleCart.bind(this));
    });

    // Cart item actions
    this.querySelectorAll('.qty-btn[data-action="increase"]').forEach(
      (button) => {
        button.addEventListener("click", () => this.updateQuantity(button, 1));
      }
    );
    this.querySelectorAll('.qty-btn[data-action="decrease"]').forEach(
      (button) => {
        button.addEventListener("click", () => this.updateQuantity(button, -1));
      }
    );
    this.querySelectorAll(".cart-item__remove").forEach((button) => {
      button.addEventListener("click", () => this.removeItem(button));
    });
    this.querySelectorAll(".suggestion__add").forEach((btn) => {
      btn.addEventListener("click", () => this.addSuggestedItem(btn));
    });

    // 👉 Modifier mon pack (redirige vers la fiche bundle en pré-remplissant)
    this.querySelectorAll(".cart-item__modify-btn").forEach((btn) => {
      btn.addEventListener("click", () => this.modifyBundle(btn));
    });

    // Quand un produit est ajouté ailleurs sur le site
    // -> On réconcilie les cadeaux (ajout auto si 1 seule variante) puis 1 seul refresh.
    // IMPORTANT: Only add this listener once to avoid infinite loops
    if (this.firstLoad == true) {
      document.addEventListener("product:added", async () => {
        try {
          this.showLoading(true);
          await this.reconcileGiftsThenRefreshOnce();
        } catch (error) {
          console.error("Erreur lors de la mise à jour du sidecart:", error);
          this.showError(
            "Une erreur est survenue lors de la mise à jour du panier."
          );
        } finally {
          this.showLoading(false);
        }
      });
    }

    this.firstLoad = false;
  }

  /**
   * Rafraîchit la section “sidecart” (HTML) + ré-initialisations
   * @param {boolean} skipEnforce - si true, n’appelle pas enforceGiftRules dans cette passe
   */
  async refreshCart(skipEnforce = false) {
    try {
      const sectionId = this.dataset.sectionId || "sidecart";
      const response = await fetch(`/?section_id=${sectionId}`);
      if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);

      const lastProgressFillWidth =
        this.querySelector(".sidecart__progress-fill")?.style.width || "0%";
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");
      const newCartContent = doc.querySelector("side-cart");

      if (!newCartContent)
        throw new Error(
          "Impossible de trouver le contenu de la section sidecart."
        );

      this.innerHTML = newCartContent.innerHTML;
      this.updateSummaryTotal?.();
      this.initProgressBar(lastProgressFillWidth);
      this.initEventListeners();
      this.initSwipers();
      this.groupCartItems();

      if (document.querySelector(".quantity_step--container")) {
        const productForm = document.querySelector("product-form");
        if (productForm) {
          await productForm.refreshQuantitySteps();
          await productForm.updateButtonPrice();
        }
      } else {
        this.classList.add("open");
      }

      const headerCount = document.querySelector(".header__cart-count");
      const newCount =
        newCartContent.querySelector(".cart-item-count")?.textContent || "0";
      if (headerCount) headerCount.textContent = newCount;
      document
        .querySelector(".header__cart-count")
        ?.setAttribute("data-cart-count-top", (newCount || "0").trim());

      if (!skipEnforce) {
        await this.enforceGiftRules(); // n'appelle refreshCart qu'en cas de changement
      }

      // Dispatch cart:refreshed event for launch-offer to listen
      document.dispatchEvent(new CustomEvent("cart:refreshed"));
    } catch (error) {
      console.error("Erreur lors du rafraîchissement du panier:", error);
      this.showError(
        "Une erreur est survenue lors de la mise à jour du panier."
      );
    }
  }

  /** Lit et normalise les paliers à partir du <script.sidecart-goals> */
  getGoals(bar, { forDisplay = false } = {}) {
    try {
      const cfgEl = bar.querySelector(".sidecart-goals");
      if (!cfgEl) return [];
      const all = JSON.parse(cfgEl.textContent || "[]").filter(
        (g) => g && typeof g.amount !== "undefined"
      );

      const arr = forDisplay ? all.filter((g) => g.show) : all;
      arr.forEach((g) => {
        g.amount = Number(g.amount) || 0;
        g.product_handle = (g.product_handle || "").toString();
        g.show = !!g.show;
      });
      return arr.sort((a, b) => a.amount - b.amount);
    } catch (e) {
      console.warn("getGoals parse error", e);
      return [];
    }
  }

  initProgressBar(lastProgressFillWidth = "0%") {
    const bar = this.querySelector(".sidecart__progress-bar");
    if (!bar) return;

    const total = Number(bar.dataset.cartTotal || 0);
    if (!Number.isFinite(total) || total < 0) return;

    const goals = this.getGoals(bar, { forDisplay: false });
    if (!goals.length) return;

    const finalMessage =
      bar.dataset.finalMessage || "Tous les objectifs atteints 🎉";
    const fill = bar.querySelector(".sidecart__progress-fill");
    const message = bar.querySelector(".sidecart__progress-message");
    const tags = bar.querySelectorAll(".sidecart__tags .tag");

    if (tags.length && tags.length !== goals.length) {
      console.warn("Mismatch goals/tags", goals.length, tags.length);
    }

    const stepAmounts = goals.map((g) => g.amount);
    const stepPercentages = [15, 38, 62, 85];
    const maxGoal = goals[goals.length - 1].amount;

    let progressWidth = "0%";
    let currentStepIndex = stepAmounts.findIndex((amount) => total < amount);
    if (currentStepIndex === -1) currentStepIndex = goals.length;

    if (total >= maxGoal) {
      progressWidth = "100%";
    } else if (total < stepAmounts[0]) {
      progressWidth = "6%";
    } else {
      const prevAmount = stepAmounts[currentStepIndex - 1];
      const nextAmount = stepAmounts[currentStepIndex] || maxGoal;
      const prevPct = stepPercentages[currentStepIndex - 1];
      const nextPct = stepPercentages[currentStepIndex] || 100;
      const pct =
        prevPct +
        ((total - prevAmount) / (nextAmount - prevAmount)) *
          (nextPct - prevPct);
      progressWidth = `${pct}%`;
    }

    if (fill) {
      fill.style.width = lastProgressFillWidth;
      requestAnimationFrame(() => {
        fill.style.width = progressWidth;
      });
    }

    const nextGoal =
      goals.find((g) => total < g.amount) || goals[goals.length - 1];
    goals.forEach((goal, i) => {
      const tag = tags[i];
      if (!tag) return;
      tag.classList.remove("completed", "active", "inactive");
      if (total >= goal.amount) {
        tag.classList.add("completed");
      } else if (goal.amount === nextGoal.amount) {
        tag.classList.add("active");
      } else {
        tag.classList.add("inactive");
      }
    });

    if (message) {
      if (total < maxGoal) {
        const g = goals.find((g) => total < g.amount);
        const remaining = (g.amount - total).toFixed(2).replace(".", ",");
        message.innerHTML = (g.message || "").replace("[remaining]", remaining);
      } else {
        message.textContent = finalMessage;
        document.querySelectorAll(".sidecart__tags .tag").forEach((el) => {
          el.classList.remove("active", "inactive");
          el.classList.add("completed");
        });
      }
    }
  }

  /**
   * Récupère /products/{handle}.js
   * @param {string} handle
   * @returns {Promise<{variants: Array, title: string}|null>}
   */
  async fetchProductByHandle(handle) {
    if (!handle) return null;
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        credentials: "same-origin",
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      console.warn("fetchProductByHandle error", handle, e);
      return null;
    }
  }

  /**
   * Calcule le total du panier sans les cadeaux
   * @param {Object} cart - objet panier depuis /cart.js
   * @returns {number} total en euros
   */
  getCartTotalWithoutGifts(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    const totalCents = cart.items.reduce((sum, item) => {
      // Exclure les items avec la propriété _gift ou le tag gift
      const isGift =
        item?.properties?._gift === "true" ||
        (item?.product_tags || []).includes("gift");

      if (isGift) return sum;

      // Utiliser final_line_price pour prendre en compte les réductions
      return sum + (item.final_line_price || 0);
    }, 0);

    return totalCents / 100;
  }

  /**
   * Applique les règles cadeau SANS rerender :
   * - retire les cadeaux dont le palier n'est plus atteint
   * - ajoute automatiquement les cadeaux à **1 seule variante** si palier atteint et non présents
   * Ne fait pas de refresh; renvoie true si des changements ont été effectués.
   */
  async reconcileGifts() {
    if (this._enforceRunning) return false;
    this._enforceRunning = true;

    try {
      const bar = this.querySelector(".sidecart__progress-bar");
      if (!bar) return false;

      const allGoals = this.getGoals(bar, { forDisplay: false });
      if (!allGoals.length) return false;

      const cart = await fetch("/cart.js", { credentials: "same-origin" }).then(
        (r) => r.json()
      );
      const totalEuros = this.getCartTotalWithoutGifts(cart);

      const unlockedHandles = new Set(
        allGoals
          .filter((g) => g.product_handle && totalEuros >= g.amount)
          .map((g) => g.product_handle)
      );
      const lockedHandles = new Set(
        allGoals
          .filter((g) => g.product_handle && totalEuros < g.amount)
          .map((g) => g.product_handle)
      );

      const giftLines = (cart.items || []).filter(
        (it) => it?.properties?._gift === "true"
      );
      const hasGiftForHandle = (handle) =>
        giftLines.some((it) => it.handle === handle);

      let changed = false;

      // 1) Retire les cadeaux devenus inéligibles
      const toRemoveKeys = [];
      for (const item of giftLines) {
        if (lockedHandles.has(item.handle)) {
          toRemoveKeys.push(item.key);
        }
      }
      for (const key of toRemoveKeys) {
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
        changed = true;
      }

      // 2) Ajoute auto les cadeaux éligibles avec **1 seule variante** si absents
      for (const handle of unlockedHandles) {
        // Re-fetch cart to get the most current state before checking
        const freshCart = await fetch("/cart.js", { credentials: "same-origin" }).then(
          (r) => r.json()
        );

        // Check if gift already exists in fresh cart data
        const giftAlreadyExists = (freshCart.items || []).some(
          (it) => it.handle === handle && it?.properties?._gift === "true"
        );

        if (giftAlreadyExists) continue;

        const prod = await this.fetchProductByHandle(handle);
        if (!prod || !Array.isArray(prod.variants)) continue;

        const availableVariants = prod.variants.filter(
          (v) => !!v && v.available
        );
        if (availableVariants.length !== 1) continue; // seulement si une seule variante dispo

        const onlyVariant = availableVariants[0];
        try {
          const randomId =
            Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15);
          await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: Number(onlyVariant.id),
              quantity: 1,
              properties: { _gift: "true", _random: randomId },
            }),
          });
          changed = true;
        } catch (e) {
          console.warn("Auto-add gift failed", handle, e);
        }
      }

      return changed;
    } catch (err) {
      console.error("reconcileGifts error:", err);
      return false;
    } finally {
      this._enforceRunning = false;
    }
  }

  /**
   * Variante “historique” appelée par refreshCart() quand on ne cherche pas absolument
   * à limiter à un seul refresh. Elle n’appelle refreshCart(true) **que si** des changements ont été faits.
   */
  async enforceGiftRules() {
    const changed = await this.reconcileGifts();
    if (changed) {
      // Important : on évite une boucle infinie en passant skipEnforce=true
      await this.refreshCart(true);
    }
  }

  /**
   * Pour les cas d’ajout produit (évènement product:added, bouton cross-sell) :
   * on réconcilie d’abord les cadeaux, PUIS on fait un unique refresh.
   */
  async reconcileGiftsThenRefreshOnce() {
    const changed = await this.reconcileGifts();
    // Peu importe changed, on rafraîchit une seule fois l’UI pour refléter le panier à jour
    await this.refreshCart(true);
    return changed;
  }

  async addSuggestedItem(buttonOrEvent) {
    const variantId = parseInt(
      buttonOrEvent?.dataset?.variantId ?? "123456789",
      10
    );
    const quantity = parseInt(buttonOrEvent?.dataset?.qty ?? "1", 10);
    if (!variantId) return;

    try {
      this.showLoading(true);
      const randomId =
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: variantId,
          quantity,
          properties: { _random: randomId },
        }),
      });

      // Après ajout, on applique la logique “1 refresh” avec cadeaux auto.
      await this.reconcileGiftsThenRefreshOnce();
    } catch (err) {
      console.error(err);
      this.showError("Erreur lors de l’ajout du produit.");
    } finally {
      this.showLoading(false);
    }
  }

  updateSummaryTotal() {}

  toggleCart(event) {
    if (
      event.type === "click" ||
      (event.type === "keydown" && ["Enter", "Space"].includes(event.key))
    ) {
      event.preventDefault();
      this.classList.toggle("open");
    }
  }

  async updateQuantity(button, change) {
    const key = button.dataset.key;
    const variantId = button.dataset.variantId;
    const input = this.querySelector(`.qty-input[data-key="${key}"]`);
    const currentQuantity = parseInt(input.value || input.textContent || 1);
    const newQuantity = currentQuantity + change;
    if (newQuantity < 0) return;

    try {
      this.showLoading(true);

      if (change > 0) {
        // Increment: add new line item with same variant but new _random
        if (!variantId) {
          console.error("No variant ID found on button");
          return;
        }

        const randomId =
          Math.random().toString(36).substring(2, 15) +
          Math.random().toString(36).substring(2, 15);
        await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: { _random: randomId },
          }),
        });
      } else {
        // Decrement: find all items with same variant_id and remove the last one
        const cart = await fetch("/cart.js", {
          credentials: "same-origin",
        }).then((r) => r.json());
        const sameVariantItems = cart.items.filter(
          (item) => String(item.variant_id) === String(variantId)
        );

        if (sameVariantItems.length > 0) {
          const lastItem = sameVariantItems[sameVariantItems.length - 1];
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: lastItem.key, quantity: 0 }),
          });
        }
      }

      await this.refreshCart();
    } catch (error) {
      console.error("Error updating quantity:", error);
      this.showError("Erreur lors de la mise à jour de la quantité.");
    } finally {
      this.showLoading(false);
    }
  }

  async removeItem(button) {
    try {
      this.showLoading(true);

      // Get the variant_id directly from cart-item container
      const cartItem = button.closest(".cart-item");
      const variantId = cartItem?.dataset.variantId;

      console.log("🗑️ Remove button clicked");
      console.log("Cart item:", cartItem);
      console.log("Variant ID:", variantId);

      if (!variantId) {
        console.error("❌ No variant_id found for removal");
        this.showError("Impossible de supprimer cet article.");
        return;
      }

      // Fetch cart and find ALL line items with this variant_id
      const cart = await fetch("/cart.js", { credentials: "same-origin" }).then(
        (r) => r.json()
      );
      const itemsToRemove = cart.items.filter(
        (item) => String(item.variant_id) === String(variantId)
      );

      console.log(
        `📦 Found ${itemsToRemove.length} items to remove with variant_id ${variantId}`
      );
      console.log(
        "Items to remove:",
        itemsToRemove.map((i) => ({ key: i.key, title: i.title }))
      );

      if (itemsToRemove.length === 0) {
        console.error("❌ No items found in cart with this variant_id");
        this.showError("Produit introuvable dans le panier.");
        return;
      }

      // Build updates object: { "key1": 0, "key2": 0, ... }
      const updates = {};
      itemsToRemove.forEach((item) => {
        updates[item.key] = 0;
      });

      console.log(
        "🔥 Removing all items in ONE request with updates:",
        updates
      );

      // Remove ALL items in ONE request using /cart/update.js
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ message: response.statusText }));
        console.error("❌ Failed to remove items:", error);
        throw new Error(
          `Failed to remove items: ${error.message || response.statusText}`
        );
      }

      console.log("✅ All items removed successfully in ONE request");
      await this.refreshCart();
    } catch (error) {
      console.error("❌ Error removing item:", error);
      this.showError("Erreur lors de la suppression de l'article.");
    } finally {
      this.showLoading(false);
    }
  }

  // === Nouveau: redirection édition bundle ===
  modifyBundle(button) {
    try {
      const lineKey = button.dataset.key;
      const productUrl = button.dataset.productUrl || "/";
      const bundleVariantId =
        Number(button.dataset.bundleVariant || "0") || null;

      let items = [];
      try {
        items = JSON.parse(button.dataset.bundleJson || "[]");
      } catch (_) {
        items = [];
      }
      const limit = items.reduce((acc, it) => acc + (parseInt(it.qty) || 0), 0);

      const payload = { lineKey, items, limit, bundleVariantId };
      try {
        sessionStorage.setItem("bundle_edit", JSON.stringify(payload));
      } catch (_) {}

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set("edit_bundle", "1");
      window.location.href = url.toString();
    } catch (e) {
      console.error("modifyBundle error", e);
      this.showError("Impossible d’ouvrir le configurateur du pack.");
    }
  }

  showLoading(isLoading) {
    const buttons = this.querySelectorAll(
      ".qty-btn, .cart-item__remove, .suggestion__add, .btn-pay, .cart-item__modify-btn"
    );
    buttons.forEach((button) => {
      button.disabled = isLoading;
      button.style.opacity = isLoading ? "0.5" : "1";
    });
  }

  showError(message) {
    const errorMessage = document.createElement("p");
    errorMessage.textContent = message;
    errorMessage.style.cssText =
      "color: red; text-align: center; margin: 10px 0;";
    errorMessage.setAttribute("aria-live", "assertive");
    this.querySelector(".sidecart__container")?.prepend(errorMessage);
    setTimeout(() => errorMessage.remove(), 5000);
  }

  async handleGiftVariantChange(e) {
    const select = e.currentTarget;
    const variantId = parseInt(select.value, 10);
    if (!variantId || isNaN(variantId)) return;

    const lineKey = select.dataset.itemKey || null;
    const currentVariantId = select.dataset.currentVariantId
      ? parseInt(select.dataset.currentVariantId, 10)
      : null;

    try {
      this.showLoading(true);

      if (lineKey && currentVariantId === variantId) {
        return;
      }

      if (lineKey) {
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: lineKey, quantity: 0 }),
        });
      }

      const randomId =
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { _gift: "true", _random: randomId },
        }),
      });

      await this.refreshCart();
    } catch (err) {
      console.error("Erreur cadeau:", err);
      this.showError("Impossible d'ajouter le cadeau. Réessayez.");
    } finally {
      this.showLoading(false);
    }
  }

  async autoAddGiftVariant(select) {
    const variantId = parseInt(select.value, 10);
    if (!variantId || isNaN(variantId)) return;

    // Check if already in cart
    if (select.dataset.itemKey) return;

    try {
      const randomId =
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { _gift: "true", _random: randomId },
        }),
      });

      // Refresh to update the select with itemKey
      await this.refreshCart();
    } catch (err) {
      console.error("Auto-add gift error:", err);
    }
  }

  // ⚠️ L'initSwipers d'origine est conservée telle quelle
  initSwipers() {
    this.querySelectorAll(".sidecart__suggestion-swiper").forEach(
      (swiperEl) => {
        if (swiperEl.swiper) {
          swiperEl.swiper.destroy(true, true);
        }
        if (swiperEl.querySelectorAll(".swiper-slide").length > 0) {
          new Swiper(swiperEl, {
            slidesPerView: "auto",
            spaceBetween: 12,
            navigation: {
              nextEl: swiperEl
                .closest(".sidecart__container")
                ?.querySelector(".swiper-button-next"),
              prevEl: swiperEl
                .closest(".sidecart__container")
                ?.querySelector(".swiper-button-prev"),
            },
            pagination: {
              el: swiperEl
                .closest(".sidecart__container")
                ?.querySelector(".swiper-pagination"),
              clickable: true,
            },
          });
        }
      }
    );
  }

  groupCartItems() {
    const cartItems = this.querySelectorAll(".cart-item");
    const groups = new Map();

    cartItems.forEach((item) => {
      const variantId =
        item.querySelector("[data-variant-id]")?.dataset.variantId;
      if (!variantId) return;

      if (!groups.has(variantId)) {
        groups.set(variantId, []);
      }
      groups.get(variantId).push(item);
    });

    groups.forEach((items, variantId) => {
      if (items.length <= 1) return;

      let totalQuantity = 0;
      let totalFinalPrice = 0;
      let totalOriginalPrice = 0;

      items.forEach((item, index) => {
        const qtyInput = item.querySelector(".qty-input");
        const qty = parseInt(qtyInput?.value || qtyInput?.textContent || 1);
        totalQuantity += qty;

        const priceContainer = item.querySelector(".cart-item__price");
        if (priceContainer) {
          const finalPriceCents = parseInt(
            priceContainer.dataset.finalLinePrice || "0"
          );
          const originalPriceCents = parseInt(
            priceContainer.dataset.originalLinePrice || "0"
          );

          totalFinalPrice += finalPriceCents;
          totalOriginalPrice += originalPriceCents;
        }

        if (index > 0) {
          item.style.display = "none";
        }
      });

      const firstItem = items[0];
      const qtyDisplay = firstItem.querySelector(".qty-input.block");
      if (qtyDisplay) {
        qtyDisplay.textContent = totalQuantity;
      }

      const qtyInputHidden = firstItem.querySelector(".qty-input.hidden");
      if (qtyInputHidden) {
        qtyInputHidden.value = totalQuantity;
      }

      const priceContainer = firstItem.querySelector(".cart-item__price");
      if (priceContainer) {
        const finalPriceEl = priceContainer.querySelector(
          ".cart-item__price--final"
        );
        const comparePriceEl = priceContainer.querySelector(
          ".cart-item__price--compare"
        );

        if (finalPriceEl) {
          finalPriceEl.textContent = new Intl.NumberFormat("fr-FR", {
            style: "currency",
            currency: "EUR",
          }).format(totalFinalPrice / 100);
        }

        if (totalOriginalPrice > totalFinalPrice) {
          if (!comparePriceEl) {
            const newComparePriceEl = document.createElement("s");
            newComparePriceEl.className = "cart-item__price--compare";
            priceContainer.insertBefore(newComparePriceEl, finalPriceEl);
          }
          const compareEl = priceContainer.querySelector(
            ".cart-item__price--compare"
          );
          if (compareEl) {
            compareEl.textContent = new Intl.NumberFormat("fr-FR", {
              style: "currency",
              currency: "EUR",
            }).format(totalOriginalPrice / 100);
            compareEl.style.display = "";
          }
        } else if (comparePriceEl) {
          comparePriceEl.style.display = "none";
        }
      }
    });
  }
}

customElements.define("side-cart", Sidecart);
